
import { SaleEditState } from "@/utils/sale-edit/sale-edit-state";
import Vue from "vue";
import AppSaleEditFinishButton from "./SaleEditFinishButton.vue";
import AppSelectSaleLayout from "@/components/SelectSaleLayout.vue";
import AppTotalBox from "./SaleEditSaveModalTotalBox.vue";
import { Sale } from "@/models/base";
import { ContactPeppolEndpoint } from "@/models/base/contact-peppol-endpoint";
import { AuthService } from "@/services/auth-service";
import { MODULE } from "@/config/modules";
import AppSalePrintExtraOptionsModal from "./SalePrintExtraOptionsModal.vue";
import { PrintContext } from "@/utils/sale-layouts/print-settings";
import {multiLineCut} from "../../utils/strings";
import AppTextAreaModal from "@/components/common/TextAreaModal.vue";
import { PeppolService } from "@/services/peppol-service";
import { DossierConfigService } from "@/services";
import AppPeppolSendOptionsModal from "./PeppolSendOptionsModal.vue";
import { isSaleTypeAllowedForBankBookings } from "@/utils/bank-booking-sale-types";

export default Vue.extend({
	data(){
		return {
			editToPrintModal: false,
			contactPeppolRegistered: false,
			contactPeppolChecking: false,
			userPeppolReady: false,
			peppolOptionsModal: false,
		};
	},
	props: {
		state: {
			type: Object as () => SaleEditState,
			required: true
		}
	},
	computed: {
		toPrintMessage(): string {
			return multiLineCut(this.sale.ToPrintMessage);
		},
		sale(): Sale {
			return this.state.sale;
		},
		salePrintExtraOptionsModalOpen():boolean {
			return this.state.printExtraOptionsModalOpen;
		},
		isSaving():boolean {
			return this.state.isSaving;
		},
		printContextPrint():PrintContext {
			return PrintContext.PRINT;
		},
		printContextDownload():PrintContext {
			return PrintContext.DOWNLOAD;
		},
		printContextSend():PrintContext{
			return PrintContext.EMAIL_SEND;
		},
		hasCalendarModal():boolean {
			return AuthService.hasModule(MODULE.AGENDA);
		},
		saleTypeName():string{
			return this.state.sale.Type.DisplayedName;
		},
		canQuickBankEdit():boolean {
			return isSaleTypeAllowedForBankBookings(this.sale.TypeID);
		},
		hideLayoutSelect():boolean {
			if (!this.state.config) return false;
			if (this.state.config.AvailableLayoutIDs.length == 0) {
				return true;
			}
			return false;
		},
		moreActions():{text:string, value:string, icon:string}[] {
			return [
				{text: this.$t("components.sales.preview").toString(), value: "preview", icon: "eye"},
				{text: this.$t("components.sales.download").toString(), value: "download", icon: "download"}
			];
		},
		isPeppolSaleType():boolean {
			return this.sale.TypeID === 1 || this.sale.TypeID === 2;
		},
		canSendViaPeppol():boolean {
			if (!this.userPeppolReady || !this.contactPeppolRegistered) return false;
			if (!this.isPeppolSaleType) return false;
			return true;
		},
		peppolFilesCount():string {
			const count = this.state.saveModalData.peppolOptions.files.length;
			return count === 1 ? this.$ct("attachment-count-single").toString() : this.$ct("attachment-count", {count}).toString();
		},
		peppolPoDisplay():string {
			const po = this.state.saveModalData.peppolOptions.po;
			return po || this.$ct("no-po").toString();
		},
		peppolEndpoints():ContactPeppolEndpoint[] {
			if (!this.sale.Contact) return [];
			return this.sale.Contact.PeppolEndpoints.filter((e:ContactPeppolEndpoint) => e.getFullID() !== "");
		},
		hasMultiplePeppolEndpoints():boolean {
			return this.peppolEndpoints.length > 1;
		},
		peppolEndpointDisplay():string {
			const selectedID = this.state.saveModalData.peppolOptions.endpointID;
			const selected = this.peppolEndpoints.find((e:ContactPeppolEndpoint) => e.getFullID() === selectedID);
			const target = selected || this.peppolEndpoints.find((e:ContactPeppolEndpoint) => e.IsDefault) || this.peppolEndpoints[0];
			if (!target) return "";
			return target.Label || target.getFullID();
		}
	},
	watch: {
		"sale.ContactID": {
			handler(){
				this.checkContactPeppolRegistration();
			},
			immediate: true
		}
	},
	methods: {
		async save() {
			try {
				let shouldShowRefundModal = this.state.sale.showCashierRefundModal();
				let done = await this.state.save();
				if (!done) return;
				this.$wf.notify(this.$t("common.success").toString(), "success", 3000, "check");
				if(shouldShowRefundModal && AuthService.hasModule(MODULE.WINKELMODULE)){
					try {
						this.state.ticketRefundModalOpen = true;
						this.state.saveModalData.pendingPeppolSend = this.state.saveModalData.sendViaPeppol;
					} catch (err) {
						console.error(err);
					}
					this.state.saveModalData.preventClose = true;
				}
				if (this.state.saveModalData.quickBankEdit && !this.sale.showCashierRefundModal()) {
					try {
						this.state.saveModalData.pendingPeppolSend = this.state.saveModalData.sendViaPeppol;
						this.state.openBankEditModal();
					} catch (err) {
						console.error(err);
					}
					this.state.saveModalData.preventClose = true;
				}
				if (!this.state.saveModalData.preventClose) {
					await this.state.sendViaPeppolIfEnabled();
					await this.$nextTick();
					this.state.onFinish();
				}
			} catch (err) {
				console.error(err);
			}
			this.state.saveModalData.preventClose = false;
		},
		onMoreAction(action: string){
			if (action === "preview") {
				this.preview();
			} else if (action === "download") {
				this.download();
			}
		},
		async send() {
			this.state.saveModalData.preventClose = true;
			let shouldShowRefundModal = this.state.sale.showCashierRefundModal();
			await this.state.save();
			if(shouldShowRefundModal && AuthService.hasModule(MODULE.WINKELMODULE)){
				try {
					this.state.ticketRefundModalOpen = true;
					this.state.callBackTask = "openSendModal";
					this.state.saveModalData.pendingPeppolSend = this.state.saveModalData.sendViaPeppol;
					this.state.saveModalData.preventClose = false;
					return;
				} catch (err) {
					console.error(err);
				}
			}
			if (this.state.saveModalData.quickBankEdit && !this.sale.showCashierRefundModal()) {
				this.state.callBackTask = "openSendModal";
				this.state.saveModalData.pendingPeppolSend = this.state.saveModalData.sendViaPeppol;
				this.state.openBankEditModal();
				this.state.saveModalData.preventClose = false;
				return;
			}
			await this.state.sendViaPeppolIfEnabled();
			this.state.saveModalData.loading = true;
			try {
				this.state.openSendModal();
			} catch (err) {
				console.error(err);
			}
			this.state.saveModalData.loading = false;
		},
		async preview(){
			try {
				await this.state.save();
			} catch (err) {
				console.error(err);
			}
			this.state.previewModal = true;
		},

		async download() {
			this.state.saveModalData.preventClose = true;
			let shouldShowRefundModal = this.state.sale.showCashierRefundModal();
			await this.state.save();
			if(shouldShowRefundModal && AuthService.hasModule(MODULE.WINKELMODULE)){
				try {
					this.state.callBackTask = "download";
					this.state.ticketRefundModalOpen = true;
					this.state.saveModalData.pendingPeppolSend = this.state.saveModalData.sendViaPeppol;
					this.state.saveModalData.preventClose = false;
					return;
				} catch (err) {
					console.error(err);
				}
			}
			if (this.state.saveModalData.quickBankEdit && !this.sale.showCashierRefundModal()) {
				this.state.callBackTask = "download";
				this.state.saveModalData.pendingPeppolSend = this.state.saveModalData.sendViaPeppol;
				this.state.openBankEditModal();
				this.state.saveModalData.preventClose = false;
				return;
			}
			await this.state.sendViaPeppolIfEnabled();
			this.state.saveModalData.loading = true;
			try {
				await this.state.download(true);
				this.state.onFinish();
			} catch (err) {
				console.error(err);
			}
			this.state.saveModalData.loading = false;
			return;
		},

		async print() {
			this.state.saveModalData.preventClose = true;
			let shouldShowRefundModal = this.state.sale.showCashierRefundModal();
			await this.state.save();
			if(shouldShowRefundModal && AuthService.hasModule(MODULE.WINKELMODULE)){
				try {
					this.state.callBackTask = "print";
					this.state.ticketRefundModalOpen = true;
					this.state.saveModalData.pendingPeppolSend = this.state.saveModalData.sendViaPeppol;
					this.state.saveModalData.preventClose = false;
					return;
				} catch (err) {
					console.error(err);
				}
			}
			if (this.state.saveModalData.quickBankEdit && !this.sale.showCashierRefundModal()) {
				this.state.callBackTask = "print";
				this.state.saveModalData.pendingPeppolSend = this.state.saveModalData.sendViaPeppol;
				this.state.openBankEditModal();
				this.state.saveModalData.preventClose = false;
				return;
			}
			await this.state.sendViaPeppolIfEnabled();
			this.state.saveModalData.loading = true;
			try {
				await this.state.print(true);
			} catch (err) {
				console.error(err);
			}
			this.state.saveModalData.loading = false;
			this.state.saveModalData.preventClose = false;
		},
		onUpdateCreator(id:number){
			this.sale.CreatorID = id;
		},

		onExtraOptionsPrinted(){
			this.state.emit("printed-with-extra-options");
		},
		onExtraOptionsClosed(){
			this.state.printExtraOptionsModalOpen = false;
			this.state.emit("printed-with-extra-options-closed");
		},
		// eslint-disable-next-line max-lines-per-function
		async checkContactPeppolRegistration(){
			if (!this.sale.Contact) {
				this.contactPeppolRegistered = false;
				this.contactPeppolChecking = false;
				return;
			}
			if (!this.state.saveModalData.peppolOptions.endpointID) {
				const def = this.peppolEndpoints.find((e:ContactPeppolEndpoint) => e.IsDefault) || this.peppolEndpoints[0];
				this.state.saveModalData.peppolOptions.endpointID = def ? def.getFullID() : "";
			}
			const endpoint = this.sale.Contact.getSearchPeppolEndpointString();
			if (!endpoint) {
				this.contactPeppolRegistered = false;
				this.contactPeppolChecking = false;
				return;
			}
			this.contactPeppolChecking = true;
			try {
				const result = await PeppolService.getParticipantInfo(endpoint);
				this.contactPeppolRegistered = result.Registered;
				if (this.contactPeppolRegistered && !this.sale.LastSentViaPeppolDate && this.isPeppolSaleType) {
					const ublConfig = DossierConfigService.getPluginConfig().UBL;
					if (ublConfig.PeppolSendEnabledByDefault) {
						this.state.saveModalData.sendViaPeppol = true;
					}
					if (ublConfig.PeppolSendCopyToBookkeeper && ublConfig.BookkeeperEmailConfigs?.sales?.Email) {
						this.state.saveModalData.peppolOptions.sendCopyToBookkeeper = true;
					}
					if (ublConfig.PeppolPoField && this.sale.ExtraFields) {
						const poValue = (this.sale.ExtraFields as any)[ublConfig.PeppolPoField] as string;
						if (poValue) {
							this.state.saveModalData.peppolOptions.po = poValue;
						}
					}
					if (ublConfig.PeppolDeliveryField && this.sale.ExtraFields) {
						const deliveryValue = (this.sale.ExtraFields as any)[ublConfig.PeppolDeliveryField] as string;
						if (deliveryValue) {
							this.state.saveModalData.peppolOptions.deliveryNumber = deliveryValue;
						}
					}
				}
			} catch (err) {
				console.error(err);
				this.contactPeppolRegistered = false;
			}
			this.contactPeppolChecking = false;
		},
		formatPeppolDate(date: Date): string {
			return date.toLocaleDateString("nl-BE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
		}
	},
	async mounted(){
		this.userPeppolReady = await PeppolService.isPeppolReadyForSending();
	},
	created(){
		if(this.sale.CreatorID==0){
			if(this.sale.Contact && this.sale.Contact.DefaultCreatorID > 0){
				this.sale.CreatorID = this.sale.Contact.DefaultCreatorID;
			}
		}
		if(this.state.config?.AskToCreateCalendarEvent == true && !this.sale.ID){
			this.state.createCalendarEventState.shouldOpenOnSave = true;
		}
	},
	components: {
		AppSaleEditFinishButton,
		AppSelectSaleLayout,
		AppTotalBox,
		AppSalePrintExtraOptionsModal,
		AppTextAreaModal,
		AppPeppolSendOptionsModal
	},
	i18nEx: {
		componentPrefix: "components.sales"
	}
});
